import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import HeroSection from './components/HeroSection';
import HeroSectionUS from './components/HeroSectionUS';
import ThankYouSection from './components/ThankYouSection';
import ThankYouSectionUS from './components/ThankYouSectionUS';
import Checkout from './components/checkout/Checkout';
import Onboarding from './pages/onboarding/Onboarding';
import Auth from './pages/auth/Auth';
import UserRegistration from './pages/auth/UserRegistration';
import AddProduct from './pages/product/AddProduct';
import ParticlesBackground from './components/ParticlesBackground';
import PresentationPage from './pages/presentation/Presentation';
import LinkFormPage from './pages/LinkFormPage';
import WppRedirect from './pages/WppRedirect';
import PrivacyPolicy from './pages/PrivacyPolicy';
import BemVindo from './pages/BemVindo';
import ReuniaoOnboarding from './pages/ReuniaoOnboarding';
import ReuniaoOnboardingObjetivos from './pages/ReuniaoOnboardingObjetivos';
import ReuniaoOnboardingFormatos from './pages/ReuniaoOnboardingFormatos';
import ReuniaoOnboardingFormatosIntro from './pages/ReuniaoOnboardingFormatosIntro';
import ReuniaoOnboardingFormatoVideo from './pages/ReuniaoOnboardingFormatoVideo';
import ReuniaoOnboardingFormatosComplexos from './pages/ReuniaoOnboardingFormatosComplexos';
import ReuniaoOnboardingScriptBianca from './pages/ReuniaoOnboardingScriptBianca';
import ReuniaoOnboardingIntegracoesCRM from './pages/ReuniaoOnboardingIntegracoesCRM';
import Teste0306 from './pages/teste/Teste0306';
import ClientsList from './pages/clients/ClientsList';
import ClientMeetings from './pages/clients/ClientMeetings';
import MeetingPresentation from './pages/clients/MeetingPresentation';
import MeetingMinutes from './pages/clients/MeetingMinutes';
import CasaPradoResidence from './pages/CasaPradoResidence';
import BioPage from './pages/bio/BioPage';
import RivieraPresentationPage from './pages/presentation/riviera/RivieraPresentation';
import MFLancamentosLP from './pages/mf-lancamentos/MFLancamentosLP';
import SofiaLP from './pages/sofia/SofiaLP';
import RivieraLP from './pages/riviera/RivieraLP';
import RivieraInventario from './pages/riviera/RivieraInventario';
import RivieraCasa from './pages/riviera/RivieraCasa';
import RivieraAdmin from './pages/riviera/RivieraAdmin';
import { RESERVA_BASE } from './pages/riviera/reserva';
import MFRealStatePage from './pages/mf-real-state/MFRealStatePage';
import PropertyListingPage from './pages/mf-real-state/PropertyListingPage';
import PropertyDetailPage from './pages/mf-real-state/PropertyDetailPage';
import VisitAgendarPage from './pages/mf-real-state/VisitAgendarPage';
import ArtDesignEventoPage from './pages/mf-real-state/ArtDesignEventoPage';
import AdminPage from './pages/mf-real-state/AdminPage';
import SejaCorretorPage from './pages/mf-real-state/SejaCorretorPage';
import QrLancamentosLP from './pages/mf-real-state/qr1/QrLancamentosLP';
import CorretorLoginPage from './pages/mf-real-state/corretor/CorretorLoginPage';
import CorretorSetPasswordPage from './pages/mf-real-state/corretor/CorretorSetPasswordPage';
import CorretorPanelPage from './pages/mf-real-state/corretor/CorretorPanelPage';
import CorretorImovelFormPage from './pages/mf-real-state/corretor/CorretorImovelFormPage';
import BlogIndexPage from './pages/mf-real-state/blog/BlogIndexPage';
import BlogPostPage from './pages/mf-real-state/blog/BlogPostPage';
import BlogCategoryPage from './pages/mf-real-state/blog/BlogCategoryPage';
import ContactModal from './pages/mf-real-state/components/ContactModal';
import PageTransition from './pages/mf-real-state/components/PageTransition';
import ScrollToTop from './pages/mf-real-state/components/ScrollToTop';
import ErrorBoundary from './pages/mf-real-state/components/ErrorBoundary';
import './App.css';

const currentHostname = window.location.hostname.toLowerCase();
const isMfDomain = currentHostname.includes('mfrealstate');
// Domínio próprio da LP Riviera (seulotenarepresa.com.br) — serve a RivieraLP no apex.
const isRivieraDomain = currentHostname.includes('seulotenarepresa');
const isBiancaPublicDomain = [
  'abianca.com.br',
  'www.abianca.com.br',
  'localhost',
  '127.0.0.1',
].includes(currentHostname);
const canAccessRivieraGoogle = isRivieraDomain || isBiancaPublicDomain;
const canAccessRivieraMeta = isRivieraDomain || isBiancaPublicDomain;
// Rota reserva da Riviera (tarefa 6f99acda): só no domínio da Riviera, em
// localhost e nas prévias do Vercel, para a aprovação. Fora do abianca.com.br de
// propósito: o pedido é que seja difícil de achar.
const canAccessRivieraReserva = isRivieraDomain
  || ['localhost', '127.0.0.1'].includes(currentHostname)
  || currentHostname.endsWith('.vercel.app');

if (isMfDomain) {
  document.title = 'MF Real Estate';
  const favicon = document.querySelector('link[rel="icon"]');
  if (favicon) favicon.href = '/mf-real-state/logos/mf-favicon-32.png';
  const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
  if (appleTouchIcon) appleTouchIcon.href = '/mf-real-state/logos/mf-logo-light.png';
} else if (isRivieraDomain) {
  // Favicon próprio da Riviera (vela do logo Momentum). O título é setado pela própria RivieraLP.
  const favicon = document.querySelector('link[rel="icon"]');
  if (favicon) favicon.href = '/riviera/favicon-32.png';
  const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
  if (appleTouchIcon) appleTouchIcon.href = '/riviera/favicon-180.png';
}

function App() {
  // Verificar se o usuário está autenticado
  const isAuthenticated = () => {
    return localStorage.getItem('userData') !== null;
  };

  // Componente para rotas protegidas
  const ProtectedRoute = ({ children }) => {
    if (!isAuthenticated()) {
      return <Navigate to="/auth" replace />;
    }
    return children;
  };

  const LocationAwareParticles = () => {
    const location = useLocation();
    const hideParticlesRoutes = ["/apresentacao", "/presentation", "/riviera", "/clientes", "/casa-prado", "/bio", "/mf-lancamentos-lp", "/mf-real-state", "/sofia", RESERVA_BASE];
    const shouldHideParticles = isMfDomain || isRivieraDomain || hideParticlesRoutes.some((path) => location.pathname.startsWith(path));
    return shouldHideParticles ? null : <ParticlesBackground />;
  };

  return (
    <Router>
      <div className="App">
        <LocationAwareParticles />
        <ScrollToTop />
        <ErrorBoundary>
        <Routes>
          {isMfDomain ? (
            <>
              <Route path="/" element={<PageTransition><MFRealStatePage /></PageTransition>} />
              <Route path="/imoveis" element={<PageTransition><PropertyListingPage /></PageTransition>} />
              <Route path="/imoveis/:id" element={<PageTransition><PropertyDetailPage /></PageTransition>} />
              <Route path="/visita/agendar/:id" element={<PageTransition><VisitAgendarPage /></PageTransition>} />
              <Route path="/art-design" element={<ArtDesignEventoPage />} />
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/categoria/:slug" element={<BlogCategoryPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/seja-corretor" element={<PageTransition><SejaCorretorPage /></PageTransition>} />
              <Route path="/corretor/login" element={<CorretorLoginPage />} />
              <Route path="/corretor/set-password" element={<CorretorSetPasswordPage />} />
              <Route path="/corretor/imovel/novo" element={<CorretorImovelFormPage />} />
              <Route path="/corretor/imovel/editar/:id" element={<CorretorImovelFormPage />} />
              <Route path="/corretor" element={<CorretorPanelPage />} />
              <Route path="/lancamentos" element={<MFLancamentosLP />} />
              {/* ⚠️ NÃO REMOVER: /qr1 é destino de QR Code IMPRESSO em material físico.
                  Remover esta rota quebra todo material já distribuído. Pode trocar o
                  CONTEÚDO (componente), nunca o path. */}
              <Route path="/qr1" element={<QrLancamentosLP />} />
            </>
          ) : (
            <Route path="/" element={isRivieraDomain ? <RivieraLP /> : <HeroSection />} />
          )}
          <Route path="/us" element={<HeroSectionUS />} />
          <Route path="/ty" element={<ThankYouSection />} />
          <Route path="/ty-us" element={<ThankYouSectionUS />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/register" element={
            <ProtectedRoute>
              <UserRegistration />
            </ProtectedRoute>
          } />
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          } />
          <Route path="/novo-produto" element={
            <ProtectedRoute>
              <AddProduct />
            </ProtectedRoute>
          } />
          <Route path="/apresentacao" element={<Navigate to="/apresentacao/10" replace />} />
          <Route path="/apresentacao/:slideNumber" element={<PresentationPage />} />
          <Route path="/presentation" element={<Navigate to="/presentation/10" replace />} />
          <Route path="/presentation/:slideNumber" element={<PresentationPage />} />
          <Route path="/link" element={<LinkFormPage />} />
          <Route path="/wpp/:telefone" element={<WppRedirect />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/bemvindo" element={<BemVindo />} />
          <Route path="/reuniao-onboarding" element={<ReuniaoOnboarding />} />
          <Route path="/reuniao-onboarding/objetivos" element={<ReuniaoOnboardingObjetivos />} />
          <Route path="/reuniao-onboarding/formatos" element={<ReuniaoOnboardingFormatosIntro />} />
          <Route path="/reuniao-onboarding/formatos/video" element={<ReuniaoOnboardingFormatoVideo />} />
          <Route path="/reuniao-onboarding/formatos/basicos" element={<ReuniaoOnboardingFormatos />} />
          <Route path="/reuniao-onboarding/formatos/complexas" element={<ReuniaoOnboardingFormatosComplexos />} />
          <Route path="/reuniao-onboarding/script-bianca" element={<ReuniaoOnboardingScriptBianca />} />
          <Route path="/reuniao-onboarding/integracoes-crm" element={<ReuniaoOnboardingIntegracoesCRM />} />
          <Route path="/teste0306" element={<Teste0306 />} />
          <Route path="/clientes" element={<ClientsList />} />
          <Route path="/clientes/:clientId" element={<ClientMeetings />} />
          <Route path="/clientes/:clientId/:meetingId" element={<MeetingPresentation />} />
          <Route path="/clientes/:clientId/:meetingId/ata" element={<MeetingMinutes />} />
          <Route path="/casa-prado" element={<CasaPradoResidence />} />
          <Route path="/sofia" element={<SofiaLP />} />
          <Route path="/riviera" element={<RivieraLP />} />
          <Route path="/riviera/gg" element={canAccessRivieraGoogle
            ? <RivieraLP flow="google-lead" />
            : <Navigate to="/" replace />
          } />
          <Route path="/riviera/meta" element={canAccessRivieraMeta
            ? <RivieraLP flow="meta-lead" />
            : <Navigate to="/" replace />
          } />
          <Route path="/gg" element={isRivieraDomain
            ? <RivieraLP flow="google-lead" />
            : <Navigate to="/" replace />
          } />
          <Route path="/meta" element={isRivieraDomain
            ? <RivieraLP flow="meta-lead" />
            : <Navigate to="/" replace />
          } />
          <Route path={RESERVA_BASE} element={canAccessRivieraReserva
            ? <RivieraLP variant="reserva" />
            : <Navigate to="/" replace />
          } />
          <Route path={`${RESERVA_BASE}/terrenos`} element={canAccessRivieraReserva
            ? <RivieraInventario tipo="terrenos" />
            : <Navigate to="/" replace />
          } />
          <Route path={`${RESERVA_BASE}/casas`} element={canAccessRivieraReserva
            ? <RivieraInventario tipo="casas" />
            : <Navigate to="/" replace />
          } />
          {/* Casa a casa e a área de cadastro do Fábio (tarefa 87c272e9), com o mesmo portão da rota reserva. */}
          <Route path={`${RESERVA_BASE}/casas/:id`} element={canAccessRivieraReserva
            ? <RivieraCasa />
            : <Navigate to="/" replace />
          } />
          <Route path={`${RESERVA_BASE}/admin`} element={canAccessRivieraReserva
            ? <RivieraAdmin />
            : <Navigate to="/" replace />
          } />
          <Route path="/riviera/apresentacao" element={<Navigate to="/riviera/apresentacao/10" replace />} />
          <Route path="/riviera/apresentacao/:slideNumber" element={<RivieraPresentationPage />} />
          <Route path="/bio" element={<BioPage />} />
          <Route path="/mf-lancamentos-lp" element={<MFLancamentosLP />} />
          <Route path="/mf-real-state" element={<PageTransition><MFRealStatePage /></PageTransition>} />
          <Route path="/mf-real-state/imoveis" element={<PageTransition><PropertyListingPage /></PageTransition>} />
          <Route path="/mf-real-state/imoveis/:id" element={<PageTransition><PropertyDetailPage /></PageTransition>} />
          <Route path="/mf-real-state/visita/agendar/:id" element={<PageTransition><VisitAgendarPage /></PageTransition>} />
          <Route path="/mf-real-state/art-design" element={<ArtDesignEventoPage />} />
          <Route path="/mf-real-state/blog" element={<BlogIndexPage />} />
          <Route path="/mf-real-state/blog/categoria/:slug" element={<BlogCategoryPage />} />
          <Route path="/mf-real-state/blog/:slug" element={<BlogPostPage />} />
          <Route path="/mf-real-state/admin" element={<AdminPage />} />
          <Route path="/mf-real-state/seja-corretor" element={<PageTransition><SejaCorretorPage /></PageTransition>} />
          <Route path="/mf-real-state/corretor/login" element={<CorretorLoginPage />} />
          <Route path="/mf-real-state/corretor/set-password" element={<CorretorSetPasswordPage />} />
          <Route path="/mf-real-state/corretor/imovel/novo" element={<CorretorImovelFormPage />} />
          <Route path="/mf-real-state/corretor/imovel/editar/:id" element={<CorretorImovelFormPage />} />
          <Route path="/mf-real-state/corretor" element={<CorretorPanelPage />} />
          {/* ⚠️ NÃO REMOVER: espelho de teste do /qr1 (QR impresso). Ver comentário no bloco isMfDomain. */}
          <Route path="/mf-real-state/qr1" element={<QrLancamentosLP />} />
        </Routes>
        </ErrorBoundary>
        {isMfDomain && <ContactModal />}
      </div>
    </Router>
  );
}

export default App;
